// client/src/scheduling/utils/iddSteps.js
// The guided IDD / Budgets flow (Hutson, 2026-10-19): 1 Budgets -> 2 Dates -> 3 Submit.
// Every decision saves right away as ONE half of the job's draft (save_draft `parts`), so
// a job is "ready" only when both halves are decided. Pure functions; the page and its
// step tables render what these decide. The model rules stay in mlBudget.js.
import { ML_STATE, declinesModel, modelHours, pyInScopeActual, splitPrediction, roundHalf } from './mlBudget';

const num = (v) => (v == null || v === '' ? 0 : Number(v) || 0);
const day = (v) => (v ? String(v).slice(0, 10) : null);
const OOS_SUBJECT = 'Other';
export const isOOSTask = (t) => t.TaskSubject === OOS_SUBJECT || Number(t.TaskOrdinal) === 999;

export const STEPS = ['budgets', 'dates', 'submit'];

const settled = (job) => !!job.RemoveAction || job.ReviewStatus === 'confirmed' || job.ReviewStatus === 'submitted';

// A budget is decided once it was saved for this cycle (or the job was confirmed /
// submitted / removed under the older flow).
export function budgetDecided(job) {
  return settled(job) || (job.ReviewStatus === 'draft' && job.NewBudgetHours != null);
}

export function datesDecided(job) {
  return settled(job) || (job.ReviewStatus === 'draft' && !!job.NewStartDeadline && !!job.NewDeadline);
}

// Ready to submit: mirrors the server's "reviewed" rule (a draft counts only its OWN
// saved dates and budget; api/schedData idd_progress_summary, swa-dataIntegrity rollup).
export function jobReady(job) {
  if (settled(job)) return true;
  if (job.ReviewStatus !== 'draft') return false;
  const s = day(job.NewStartDeadline), e = day(job.NewDeadline);
  return !!s && !!e && e > s && num(job.NewBudgetHours) > 0;
}

// Dates wait for budgets (Hutson, 2026-10-21; Dan Eison could date a job with no budget):
// the Dates step is locked until every job has a budget, the first time. Once the manager has
// decided or submitted any dates, a job that still needs a budget (one that joined later)
// locks only its own row. -> { locked, pending } (pending = jobs still needing a budget).
export function datesStepLocked(jobs) {
  const pending = jobs.filter(j => !budgetDecided(j)).length;
  const started = jobs.some(j => j.ReviewStatus === 'submitted'
    || (j.ReviewStatus === 'draft' && !!j.NewStartDeadline && !!j.NewDeadline));
  return { locked: pending > 0 && !started, pending };
}

// Last year's job logged no hours at all (Dan Eison, 2026-10-21): ~450 Fall jobs, nearly all
// closed last year that way (176 marked Not Doing CY, 157 never started, 87 completed with no
// time). The Budgets step offers "Not needed?" -> the Remove choice.
export function lastYearEmpty(job) {
  return job.PEJobIndex_PY != null && job.PYActual != null && Number(job.PYActual) === 0;
}

// What a not-ready job still needs, for the Submit step's list.
export function missingParts(job) {
  if (jobReady(job)) return [];
  const out = [];
  if (!budgetDecided(job) || (job.ReviewStatus === 'draft' && num(job.NewBudgetHours) <= 0)) out.push('budget');
  const s = day(job.NewStartDeadline), e = day(job.NewDeadline);
  if (!datesDecided(job)) out.push('dates');
  else if (job.ReviewStatus === 'draft' && !(s && e && e > s)) out.push('dates');
  return out;
}

export function stepCounts(jobs) {
  const c = { total: jobs.length, budgets: 0, dates: 0, ready: 0, submitted: 0 };
  for (const j of jobs) {
    if (budgetDecided(j)) c.budgets++;
    if (datesDecided(j)) c.dates++;
    if (jobReady(j)) c.ready++;
    if (j.ReviewStatus === 'submitted') c.submitted++;
  }
  return c;
}

// The step to open on: the first one with work left.
export function firstOpenStep(counts) {
  if (counts.total === 0) return 'budgets';
  if (counts.budgets < counts.total) return 'budgets';
  if (counts.dates < counts.total) return 'dates';
  return 'submit';
}

// Last year's actual hours as this year's budget (jobs without a model number): the SAME
// total the Last year column shows (in-scope PY WIP, Other / OOS left out, to the half
// hour), split across this year's tasks exactly as the model's number is (by last year's
// hours, largest remainder over half hours). Before 2026-10-19 each task was rounded to a
// whole hour with a 1h minimum, which drifted from the column (141.25h -> "143h").
// -> { map: {subject: remaining}, total, target } or null when last year logged nothing.
// total = target unless a task already has more hours logged than its share (it keeps them).
export function pyBudgetSuggestion(taskRows) {
  const actual = pyInScopeActual(taskRows, null).hours;
  if (!actual || actual <= 0) return null;
  const split = splitPrediction(actual, taskRows);
  if (!split) return null;
  return { map: split.remaining, total: split.total, target: roundHalf(actual) };
}

// The Budgets step's buttons for an undecided row, in order (the first is primary).
//   kind: 'model' | 'keep' | 'py' | 'enter';  hours for model / keep / py;  why: Keep
//   turns the model down (offer the optional reason).
export function budgetOptions({ budget, ml, py }) {
  const b = num(budget);
  const keep = { kind: 'keep', hours: b };
  if (ml && ml.split) {
    const model = { kind: 'model', hours: modelHours(ml.pred) };
    const keepWhy = { ...keep, why: declinesModel(b, ml.pred) };
    switch (ml.state) {
      case ML_STATE.NO_BUDGET: return [model, { kind: 'enter' }];
      // Agrees: Keep first; the model's exact number second, unless it is the same.
      case ML_STATE.AGREES: return Math.abs(model.hours - b) > 0.25 ? [keep, model] : [keep];
      case ML_STATE.SET_THIS_YEAR: return [keepWhy, model];
      default: return [model, keepWhy];            // close, stale
    }
  }
  if (ml && !ml.split) return b > 0 ? [{ ...keep, why: declinesModel(b, ml.pred) }] : [{ kind: 'enter' }];
  if (b > 0) {
    // No model: last year's hours lead when they say something different (> max(2h, 10%)
    // off), Keep second; when they're close, Keep leads and last year is second; the same
    // number, Keep alone.
    if (!py) return [keep];
    const lastYear = { kind: 'py', hours: py.total };
    const gap = Math.abs(py.total - b);
    if (gap > Math.max(2, 0.1 * b)) return [lastYear, keep];
    return gap > 0.25 ? [keep, lastYear] : [keep];
  }
  return py ? [{ kind: 'py', hours: py.total }, { kind: 'enter' }] : [{ kind: 'enter' }];
}

// The Dates step's buttons for an undecided row: Accept the AI dates and / or Keep the dates
// PE already has (when they are valid).
export function datesOptions({ job, ai }) {
  const s = day(job.StartDeadline), e = day(job.Deadline);
  const current = s && e && e > s ? { startdate: s, enddate: e } : null;
  const out = [];
  if (ai && ai.startdate && ai.enddate) out.push({ kind: 'ai', startdate: day(ai.startdate), enddate: day(ai.enddate) });
  if (current && !(ai && day(ai.startdate) === current.startdate && day(ai.enddate) === current.enddate)) {
    out.push({ kind: 'keep', ...current });
  }
  return out;
}

// The financial statements a return waits for (Kate Fluker #743; queryIDDJobs' FS* fields,
// api/shared/iddDependencies.js): the return starts after the FS ends. end = the FS's IDD
// decision this cycle when decided, else Practice Engine's. -> { name, manager, start, end,
// decided, linked } or null.
export function fsDependency(job) {
  if (!job || job.FSJobIndex == null) return null;
  return {
    name: job.FSJobName, manager: job.FSManagerName, start: day(job.FSStart), end: day(job.FSEnd),
    decided: !!job.FSDecided, linked: !!job.FSLinked,
  };
}

// True when a start is on or before the DECIDED end of the FS the return waits for (an
// undecided FS's PE dates may be last year's, so they don't warn).
export function startsBeforeFS(job, start) {
  const fs = fsDependency(job);
  const s = day(start);
  return !!(fs && fs.decided && fs.end && s && s <= fs.end);
}

// A decision the manager can take back (Change, or the bulk Undo): saved on this cycle's
// draft. Confirmed / submitted / removed jobs can't be undone from here.
export function undoable(job, half) {
  return job.ReviewStatus === 'draft' && !job.RemoveAction && (half === 'dates' ? datesDecided(job) : budgetDecided(job));
}

// Bulk "Apply recommended" (Hutson, 2026-10-20): each ticked row takes ITS first option,
// so a mix of model / last year / keep rows is one click. Rows already decided, and rows
// with nothing to suggest (Enter budget first, or no dates to offer), are skipped.
// rows: [{ pe, options, decided }] -> { picks: [{ pe, option }], mix: { kind: n },
// noSuggestion, decided }.
export function recommendedPlan(rows) {
  const plan = { picks: [], mix: {}, noSuggestion: 0, decided: 0 };
  for (const { pe, options, decided } of rows) {
    if (decided) { plan.decided++; continue; }
    const first = options && options[0];
    if (!first || first.kind === 'enter') { plan.noSuggestion++; continue; }
    plan.picks.push({ pe, option: first });
    plan.mix[first.kind] = (plan.mix[first.kind] || 0) + 1;
  }
  return plan;
}

const MIX_WORDS = {
  budgets: [['model', 'use the model'], ['py', 'use last year'], ['keep', 'keep current']],
  dates: [['ai', 'accept AI dates'], ['keep', 'keep current dates']],
};
const NO_SUGGESTION = { budgets: 'no suggestion (enter these by hand)', dates: 'no dates to use (type them in)' };

// The plan in words, for the button's tooltip and the confirm:
//   mix: ['130 use the model', '92 use last year', ...]; skipped: ['12 with no suggestion ...'].
export function describePlan(plan, step) {
  const mix = MIX_WORDS[step].filter(([k]) => plan.mix[k]).map(([k, w]) => `${plan.mix[k]} ${w}`);
  const skipped = [];
  if (plan.noSuggestion) skipped.push(`${plan.noSuggestion} with ${NO_SUGGESTION[step]}`);
  if (plan.decided) skipped.push(`${plan.decided} already decided`);
  return { mix, skipped };
}

// The end of the AI's extension window (DJB, 2026-10-21): jobs mostly worked after the cycle's
// AI cutoff last year (likely extensions) get dates after it, up to Oct 15 of the cutoff's
// year. Null when the cutoff is within a week of that already (the spring cycles). Mirrors
// api/rprsIDD/aiSchedule.js extensionEndFor. -> 'YYYY-MM-DD' or null.
export function extensionCutoff(scheduleUpTo) {
  const c = day(scheduleUpTo);
  if (!c) return null;
  const e = `${c.slice(0, 4)}-10-15`;
  return (Date.parse(`${e}T12:00:00Z`) - Date.parse(`${c}T12:00:00Z`)) >= 7 * 86400000 ? e : null;
}

// True when a job's budget moved since its AI dates were made: the run records the
// remaining hours it scheduled with (recommendation.Hours, 2026-10-19 onward).
export function aiHoursStale(rec, { budget, wip }) {
  if (!rec || rec.Hours == null) return false;
  const remaining = Math.max(0, num(budget) - num(wip));
  return Math.abs(num(rec.Hours) - remaining) > 0.5;
}

// The audit Source for a budget decision (contract section 6).
export function budgetSource(kind, { pred, budget, appliedModelThisSession } = {}) {
  if (kind === 'model') return 'ml-suggestion';
  if (kind === 'py') return 'py-suggestion';
  if (kind === 'keep') return pred && declinesModel(budget, pred) ? 'ml-declined' : 'confirm-no-change';
  return appliedModelThisSession ? 'ml-tweak' : 'manual';      // typed
}

export function datesSource(kind, { bulk = false, acceptedAIThisSession = false } = {}) {
  if (kind === 'ai') return bulk ? 'ai-bulk' : 'ai-accept';
  if (kind === 'keep') return 'confirm-no-change';
  return acceptedAIThisSession ? 'ai-tweak' : 'manual';         // typed
}
